'use client'

import { Button } from '@/core/components/form/button/button'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect } from 'react'
import { LuBell, LuSearch, LuUserRound } from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import NotificationSheet from '@/features/veluvit/components/account/notification-sheet'
import { ICON_BTN } from '@/features/veluvit/components/ui/pill-class'
import { to } from '@/features/veluvit/lib/route'
import {
  loadNotifications,
  syncNotificationIdentity,
  useNotificationStore,
} from '@/features/veluvit/services/notification.service'
import { openSheet } from '@/features/veluvit/stores/overlay.store'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

/**
 * 머리줄 오른쪽 검색 · 알림 · 마이 — 모바일 머리줄(page-header)과 PC 상단 내비(gnb)가 같은 부품을 쓴다.
 * 마이는 로그인 · 비로그인 모두 같은 사람 아이콘이고, 로그인은 마이 화면에서 한다.
 * 알림은 누르면 알림함 시트. 안 읽은 알림이 있으면 종 위에 금색 점(좁은 자리라 숫자 대신 점).
 */
export default function AccountIcons({ className }: { className?: string }) {
  const pathname = usePathname()
  const onMy = pathname.startsWith('/my')
  const userId = useSessionStore((s) => s.data?.user?.id)
  const identity = useSessionStore((s) => s.identity)
  const unread = useNotificationStore((s) =>
    userId && s.identity === identity ? s.unread : 0
  )

  // 계정이 생기거나 바뀌면 안 읽은 수를 받아 둔다(게스트도 계정이다)
  useEffect(() => {
    syncNotificationIdentity()
    if (userId) void loadNotifications()
  }, [identity, userId])

  return (
    <div
      className={cn(
        'flex flex-none items-center gap-1 over10:gap-3',
        className
      )}
    >
      <Link
        href={to('/search')}
        aria-label="검색"
        aria-current={pathname === '/search' ? 'page' : undefined}
        className={cn(ICON_BTN, 'aria-[current=page]:bg-foreground/15')}
      >
        <LuSearch aria-hidden="true" className="size-5.5" />
      </Link>
      <Button
        type="button"
        aria-label={unread ? `알림 — 안 읽은 알림 ${unread}개` : '알림'}
        onClick={() =>
          openSheet(<NotificationSheet />, {
            label: '알림',
            key: 'notifications',
          })
        }
        className={cn(ICON_BTN, 'relative')}
        variant="bare"
      >
        <LuBell aria-hidden="true" className="size-5.5" />
        {unread > 0 && (
          <span
            aria-hidden="true"
            className="absolute top-1.5 right-1.5 size-2 rounded-full bg-sub shadow-[0_0_0_2px_var(--color-background)]"
          />
        )}
      </Button>
      <Link
        href="/my"
        aria-label="마이"
        aria-current={onMy ? 'page' : undefined}
        className={cn(ICON_BTN, 'aria-[current=page]:bg-foreground/15')}
      >
        <LuUserRound aria-hidden="true" className="size-5.5" />
      </Link>
    </div>
  )
}
